import {UIGameCardController} from './ui_game_card/UIGameCardController'
import {UIGamePlayersController} from './ui_game_players/UIGamePlayersController'
import {UIGameCanvasController} from './UIGameCanvasController'
import {UIGameMapController} from './ui_game_map/UIGameMapController'
import {UIGameActionsController} from './ui_game_actions/UIGameActionsController'
import {UIGameToolTipController} from './ui_game_tooltip/UIGameToolTipController'
import {UIGameEventController} from './UIGameEventController'
import {GameClientState} from '../../../../shared/GameClientState'
import {BuildGameConfigurations, ModeSettingEnum} from '@colonist/game-base-types'
import {SocketGameSend} from '../socket/SocketGameSend'
import {UIAdController} from '../ui_ad/UIAdController'
import {socket} from '../EntryShared'
import {UIGamePauseController} from './UIGamePauseController'
import {UIGameLayout} from './UIGameLayout'
import {CA} from '../analytics/Analytics'
import {UIGameController} from './UIGameController'
import {UIGameDiceController} from './ui_game_dice/UIGameDiceController'
import {Point, TradeCreationData, TradeResponse} from '@colonist/game-mechanic-validators'
import {UIGamePlayerAvatarController} from './UIGamePlayerAvatarController'
import {GameState, NestedReadonly, PlayerColor, TradeId} from '@colonist/game-states'
import {UIGameBeginnerModeController} from './ui_game_beginner_mode/UIGameBeginnerModeController'
import {UIGameBeginnerHintsController} from './ui_game_beginner_hints/UIGameBeginnerHintsController'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {ClientUserState} from '../ClientUserState'
import {renderUIGame, createGameStore, GameStore, GameUserSettingsProps} from '@colonist/ui-game'
import {StorageWrapper} from '../StorageWrapper'
import {Sound} from '../Sound'
import {PIXI} from '@colonist/pixi'
import {UIGameMapBottomLogo} from './ui_game_map/UIGameMapBottomLogo'
import ColonistRushLogo from '@static/images/colonist_rush_banner_alpha.png'
import ColonistRushGradient from '@static/images/colonist_rush_background_gradient.png'

export interface UIGameManagerMethods {
    canvasController: UIGameCanvasController
    gameController: UIGameController
    socketGameSend: SocketGameSend
    pauseController: UIGamePauseController
    resetGameUIToRebuildOnReconnect: () => void
    gameState: GameClientState
    mapController: UIGameMapController
    playersController?: UIGamePlayersController
    eventController: UIGameEventController
}

export abstract class UIGameManager implements UIGameManagerMethods {
    gameController: UIGameController

    gameState: GameClientState
    gameLayout: UIGameLayout
    socketGameSend: SocketGameSend

    eventController: UIGameEventController
    canvasController: UIGameCanvasController
    mapController: UIGameMapController

    playerAvatarController: UIGamePlayerAvatarController
    playersController?: UIGamePlayersController
    actionsController: UIGameActionsController
    diceController?: UIGameDiceController
    toolTipController: UIGameToolTipController
    adController: UIAdController | undefined
    cardController: UIGameCardController

    pauseController: UIGamePauseController

    originalMapPosition: {location: Point, zoom: Point} | undefined

    gameStore: GameStore

    constructor(adController: UIAdController | undefined) {
        this.eventController = new UIGameEventController()
        this.playerAvatarController = new UIGamePlayerAvatarController(this)

        this.adController = adController
        this.eventController.registerUIGameMechanic(CA)
    }

    abstract openShareLinkMobileModal(link: string): void

    initializeSocket(gameState: GameClientState) {
        this.gameState = gameState
        this.socketGameSend = new SocketGameSend(socket.controller.sender, () => this.gameState.serverId)
    }

    resetGameUIToRebuildOnReconnect() {
        if(!this.canvasController.builtGame()) return
        const mapLocation = new Point(this.mapController.mapView.left, this.mapController.mapView.top)
        const mapZoom = new Point(this.mapController.mapView.scale.x, this.mapController.mapView.scale.y)
        this.originalMapPosition = {
            location: mapLocation,
            zoom: mapZoom,
        }
        this.canvasController.app.stage.removeChildren()
        this.eventController = new UIGameEventController()
    }

    buildGame(configurations: BuildGameConfigurations): void {
        this.gameStore = createGameStore({
            gameGeneral: {
                initialMyColor: this.gameController.myColor,
                initialTimeLeftInSeconds: this.gameController.timeLeftInCurrentState,
            },
            gameState: {
                initialGameState: configurations.gameState,
            },
            gameUserStates: {
                initialUserStates: configurations.playerUserStates,
            },
            gameVote: {
                onVoteAccept: (voteId) => {
                    this.socketGameSend.clickedVote(voteId, true)
                },
                onVoteReject: (voteId) => {
                    this.socketGameSend.clickedVote(voteId, false)
                },
            },
            gameSettings: {
                initialGameSettings: configurations.gameSettings,
            },
            gameMode: {
                initialGameModeData: this.gameState.gameModeData,
            },
            gameDetails: {
                matchmakingMatchType: configurations.gameDetails.matchmakingMatchType,
                isRanked: configurations.gameDetails.isRanked,
                isDiscord: configurations.gameDetails.isDiscord,
                playOrder: configurations.playOrder,
                isCanvasFullWidth: this.canvasController.responsiveController.isCanvasFullWidth,
            },
            gameUserSettings: this.getGameUserSettings(),
            gameTrade: {
                sendUpdateTradeResponse: (tradeResponse: TradeResponse) => {
                    this.socketGameSend.updateTradeResponse(tradeResponse)
                },
                onClickExecuteTrade: (tradeId: TradeId, playerColor: PlayerColor) => {
                    this.socketGameSend.executeTrade(tradeId, playerColor)
                },
                onClickCreateTrade: (tradeCreationData: TradeCreationData) => {
                    this.socketGameSend.createTrade(tradeCreationData)
                },
                onClickToggleEmbargoAgainstPlayer: (color: PlayerColor) => {
                    this.socketGameSend.toggleEmbargoAgainstPlayer(color)
                },
                sendCancelAction: () => {
                    this.socketGameSend.cancelAction()
                },
            },
            gamePause: {
                togglePause: () => {
                    this.socketGameSend.togglePause()
                },
            },
            gameReplay: {
                isReplay: configurations.isReplay ?? false,
            },
        })

        renderUIGame({
            gameManager: this,
            options: {
                initialCanResign: this.gameState.canResign,
                isAfterLiveGame: configurations.isAfterLiveGame,
                isChatEnabled: !UIUtilsEjsVariables.isUserOnDiscordMobile,
            },
            gameStore: this.gameStore,
        })
    }

    protected buildOptionalMechanics(gameState: NestedReadonly<GameState>) {
        if(this.gameController.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P) {
            this.initBeginnerMode()
            return
        }

        const areBeginnerHintsEnabled = !gameState.mechanicBarbarianInvasionState && !gameState.mechanicPirateState
        if(gameState.mechanicSettlementState
                    && gameState.mechanicCityState
                    && gameState.mechanicRoadState
                    && areBeginnerHintsEnabled) this.initBeginnerHintsController()
    }

    protected initBeginnerMode() {
        new UIGameBeginnerModeController(this)
        this.cardController.initBeginnerModeHelper()
    }

    private initBeginnerHintsController() {
        if(ClientUserState.userState != undefined && !ClientUserState.userState.isBeginner()) return
        if(UIUtilsEjsVariables.isUserOnMobileApp) return

        new UIGameBeginnerHintsController(this)
    }

    private getGameUserSettings(): GameUserSettingsProps {
        const isAntialiasEnabledDefault = window.devicePixelRatio * window.innerWidth <= 2000
        const resolutionDefault = (() => {
            const allowedResolutions = [0.5, 1, 1.5, 2]
            const rawResolution = UIUtilsEjsVariables.isUserOnDiscord ? 2 : window.devicePixelRatio
            return allowedResolutions.reduce((prev, curr) => curr <= rawResolution ? curr : prev, 0.5)
        })()

        return {
            initialVolume: StorageWrapper.getMasterVolume(Sound.DEFAULT_VOLUME),
            initialIsAnimationsEnabled: StorageWrapper.getAnimation() ?? true,
            initialIsAntialiasEnabled: StorageWrapper.getAntialias() ?? isAntialiasEnabledDefault,
            initialIsKeyboardShortcutsEnabled: StorageWrapper.getKeyboardShortcuts() ?? true,
            initialResolution: StorageWrapper.getResolution() ?? resolutionDefault,
            initialPowerPreference: StorageWrapper.getPowerPreference(),
            setCanvasOptions: (options: Partial<PIXI.IApplicationOptions>) => {
                this.canvasController.updateCanvasSettings(options)
            },
        }
    }

    protected addColonistRushBranding() {
        const gradientTexture = PIXI.Texture.from(ColonistRushGradient)
        this.canvasController.setBackgroundSprite(gradientTexture)

        const logoTexture = PIXI.Texture.from(ColonistRushLogo)
        const logoTagXOffset = 33
        new UIGameMapBottomLogo(this.mapController, logoTexture, logoTagXOffset)
    }
}
